import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Loader2, Building2, FlaskConical, Target, Mail, Globe, ExternalLink } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function OperonCard() {
  const { cardId } = useParams();
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchCard();
  }, [cardId]);

  const fetchCard = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/operon-card/${cardId}`);
      if (res.ok) {
        const data = await res.json();
        setCard(data);
      } else if (res.status === 404) {
        setError("Card not found");
      } else {
        setError("Failed to load card");
      }
    } catch (e) {
      setError("Failed to load card");
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-teal-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-white" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-teal-900 flex items-center justify-center">
        <div className="text-center text-white">
          <h1 className="text-2xl font-bold mb-2">Card Not Found</h1>
          <p className="text-slate-300">This card may have been removed or the link is invalid.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-teal-900 flex items-center justify-center p-4">
      {/* Card Container */}
      <div className="w-full max-w-lg">
        {/* Operon Branding */}
        <div className="text-center mb-6">
          <span className="text-teal-400 text-sm font-medium tracking-wide">OPERON</span>
        </div>
        
        {/* The Card */}
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Header with gradient */}
          <div className="bg-gradient-to-r from-teal-600 to-teal-700 p-6 text-white">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center">
                <Building2 className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold">{card.company_name}</h1>
                <p className="text-teal-100 text-sm mt-1">{card.one_liner}</p>
              </div>
            </div>
          </div>
          
          {/* Card Body */}
          <div className="p-6 space-y-6">
            {/* Pitch Blurb */}
            <div className="bg-gradient-to-r from-teal-50 to-cyan-50 p-4 rounded-xl border border-teal-100">
              <p className="text-slate-800 italic leading-relaxed">
                "{card.pitch_blurb}"
              </p>
            </div>
            
            {/* Key Details */}
            <div className="grid grid-cols-2 gap-4">
              {/* Indications */}
              {card.indications && card.indications.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 text-sm text-slate-500 mb-2">
                    <Target className="w-4 h-4" />
                    <span>Focus Areas</span>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {card.indications.map((ind, i) => (
                      <Badge key={i} variant="secondary" className="bg-teal-100 text-teal-700 capitalize">
                        {ind}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Modality */}
              {card.modality && (
                <div>
                  <div className="flex items-center gap-2 text-sm text-slate-500 mb-2">
                    <FlaskConical className="w-4 h-4" />
                    <span>Modality</span>
                  </div>
                  <Badge variant="outline" className="border-slate-300 capitalize">
                    {card.modality}
                  </Badge>
                </div>
              )}
            </div>
            
            {/* Stage */}
            {card.funding_stage && (
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <span className="text-sm text-slate-600">Funding Stage</span>
                <Badge className="bg-slate-800 text-white capitalize">
                  {card.funding_stage.replace(/-/g, ' ')}
                </Badge>
              </div>
            )}
            
            {/* Actions */}
            <div className="flex gap-3 pt-2">
              {card.contact_email && (
                <Button 
                  onClick={() => window.location.href = `mailto:${card.contact_email}`}
                  className="flex-1 bg-teal-600 hover:bg-teal-700"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Get in Touch
                </Button>
              )}
              {card.website && (
                <Button 
                  variant="outline"
                  onClick={() => window.open(card.website, '_blank')}
                >
                  <Globe className="w-4 h-4 mr-2" />
                  Website
                </Button>
              )}
            </div>
          </div>
          
          {/* Footer */}
          <div className="bg-slate-50 px-6 py-4 border-t border-slate-100">
            <div className="flex items-center justify-between">
              <span className="text-xs text-slate-400">
                Shared via Operon
              </span>
              <a 
                href="https://operon.io" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-xs text-teal-600 hover:text-teal-700 flex items-center gap-1"
              >
                Learn more <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </div>
        </div>
        
        {/* Bottom text */}
        <p className="text-center text-slate-400 text-xs mt-6">
          Operon helps biotech founders connect with the right investors.
        </p>
      </div>
    </div>
  );
}
